import { createSlice } from '@reduxjs/toolkit';
import { readQueryParams } from 'src/utils/QueryParamsHelper';
import { PENDING_APPROVALS_KEY } from '../constant';
import { IPendingApprovalItem } from '../interface';

interface IApprovalsSlice {
  panList: IPendingApprovalItem[];
  idCardList: IPendingApprovalItem[];
  selectedIdApprovalItem: IPendingApprovalItem | null;
  currentSelectedPanIndex: number;
  selectedPanApprovalItem: IPendingApprovalItem | null;
  loading: boolean;
  totalPanPendingApprovals: number;
  totalIdCardPendingApprovals: number;
  approvalOrRejectionInProgress: boolean;
  idCardValidAsOf: string;
}

const initialState = {
  panList: [],
  idCardList: [],
  selectedIdApprovalItem: null,
  currentSelectedPanIndex: 0,
  selectedPanApprovalItem: null,
  loading: true,
  totalPanPendingApprovals: 0,
  totalIdCardPendingApprovals: 0,
  approvalOrRejectionInProgress: false,
  idCardValidAsOf: ''
} as IApprovalsSlice;

const ApprovalsSlice = createSlice({
  name: 'approvals',
  initialState,
  reducers: {
    setPanCardPendingList: (state, action) => {
      const { list, totalPendingApprovals } = action.payload;
      const selectedId = state?.selectedPanApprovalItem?.referenceId;
      let currentSelectedPanIndex = state.currentSelectedPanIndex;
      let selectedPanApprovalItem = null;
      if (state.approvalOrRejectionInProgress) {
        state.approvalOrRejectionInProgress = false;
        if (currentSelectedPanIndex <= list.length - 1) {
          selectedPanApprovalItem = list[currentSelectedPanIndex];
        } else if (list.length && currentSelectedPanIndex > list.length - 1) {
          selectedPanApprovalItem = list.slice(-1)?.[0];
          currentSelectedPanIndex = list.length - 1;
        } else {
          selectedPanApprovalItem = null;
          currentSelectedPanIndex = 0;
        }
      } else if (selectedId) {
        const index = list.findIndex(
          (item: IPendingApprovalItem) => item.referenceId === selectedId
        );
        if (index > -1) {
          selectedPanApprovalItem = list[index];
          currentSelectedPanIndex = index;
        }
      } else if (!state.selectedPanApprovalItem && list.length) {
        selectedPanApprovalItem = list[0];
        currentSelectedPanIndex = 0;
      }
      state.selectedPanApprovalItem = selectedPanApprovalItem;
      state.currentSelectedPanIndex = currentSelectedPanIndex;
      state.panList = list;
      state.totalPanPendingApprovals = totalPendingApprovals;
      state.loading = false;
    },
    setIDCardPendingList: (state, action) => {
      const { list, totalPendingApprovals, idCardValidAsOf } = action.payload;
      const { [PENDING_APPROVALS_KEY]: queryParams } = readQueryParams();
      const selectedId = queryParams?.selectedApprovalItem;
      const hasSearchQuery = queryParams?.query;
      if (state.approvalOrRejectionInProgress) {
        state.approvalOrRejectionInProgress = false;
        state.selectedIdApprovalItem = null;
      } else if (selectedId) {
        const index = list.findIndex(
          (item: IPendingApprovalItem) => item.referenceId === selectedId
        );
        if (index > -1) {
          state.selectedIdApprovalItem = list[index];
        }
      } else if (!state.selectedIdApprovalItem && list.length && !hasSearchQuery) {
        state.selectedIdApprovalItem = list[0];
      }
      state.idCardList = list;
      state.totalIdCardPendingApprovals = totalPendingApprovals;
      state.loading = false;
      state.idCardValidAsOf = idCardValidAsOf;
    },
    setSelectedIdApprovalItem: (state, action) => {
      state.selectedIdApprovalItem = action.payload;
    },
    setSelectedPanApprovalItem: (state, action) => {
      state.selectedPanApprovalItem = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setApprovalOrRejectionInProgress: (state, action) => {
      state.approvalOrRejectionInProgress = action.payload;
    },
    setCurrentSelectedPanIndex: (state, action) => {
      state.currentSelectedPanIndex = action.payload;
    }
  }
});

export const {
  setPanCardPendingList,
  setIDCardPendingList,
  setSelectedIdApprovalItem,
  setSelectedPanApprovalItem,
  setLoading,
  setApprovalOrRejectionInProgress,
  setCurrentSelectedPanIndex
} = ApprovalsSlice.actions;

export default ApprovalsSlice.reducer;
